import { $layoutY } from '#packages/layout';

import type { LayoutY } from '#packages/layout';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompLayout, CompRef } from 'types/documentServices';

export function getLayout(editorAPI: EditorAPI, compRef: CompRef): CompLayout {
  return editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
    compRef,
  );
}

const GAP_MATCHING_THRESHOLD = 10;
export function isMatchingSiblingGaps(
  gap: LayoutY,
  gapAbove: LayoutY | null,
  gapBelow: LayoutY | null,
) {
  const aboveGapDelta = gapAbove
    ? Math.abs(gap.height - gapAbove.height)
    : null;
  const belowGapDelta = gapBelow
    ? Math.abs(gap.height - gapBelow.height)
    : null;

  const isMatchingGapAbove =
    aboveGapDelta !== null &&
    aboveGapDelta < GAP_MATCHING_THRESHOLD &&
    gapAbove.height > 0;
  const isMatchingGapBelow =
    belowGapDelta !== null &&
    belowGapDelta < GAP_MATCHING_THRESHOLD &&
    gapBelow.height > 0;

  if (
    isMatchingGapAbove &&
    isMatchingGapBelow &&
    aboveGapDelta !== belowGapDelta
  ) {
    return {
      isMatchingGapAbove: true,
      isMatchingGapBelow: false,
    };
  }

  return {
    isMatchingGapAbove,
    isMatchingGapBelow,
  };
}

export function getGapAboveComponent(
  comp: CompLayout,
  container: CompLayout,
  siblings: CompLayout[],
) {
  const closestSiblingAbove = $layoutY(comp).getClosest(
    siblings.filter((sibling) => $layoutY(sibling).isAbove(comp)),
    {},
  );

  return getLayoutYBetweenComps(closestSiblingAbove, comp, container);
}

export function getGapBelowComponent(
  comp: CompLayout,
  container: CompLayout,
  siblings: CompLayout[],
) {
  const closestSiblingBelow = $layoutY(comp).getClosest(
    siblings.filter((sibling) => $layoutY(sibling).isBelow(comp)),
    {},
  );

  return getLayoutYBetweenComps(comp, closestSiblingBelow, container);
}

function getLayoutYBetweenComps(
  componentAbove: LayoutY | null,
  componentBelow: LayoutY | null,
  container: LayoutY | null,
): LayoutY | null {
  if (componentAbove && componentBelow) {
    return getLayoutYByTwoPoints(
      $layoutY(componentAbove).bottomY,
      componentBelow.y,
    );
  }

  if (componentAbove && !componentBelow) {
    return getLayoutYByTwoPoints(
      $layoutY(componentAbove).bottomY,
      $layoutY(container).bottomY,
    );
  }

  if (!componentAbove && componentBelow) {
    return getLayoutYByTwoPoints(container.y, componentBelow.y);
  }

  return null;
}

function getLayoutYByTwoPoints(y1: number, y2: number): LayoutY {
  return { y: y1, height: y2 - y1 };
}
